// Services — service monitor.
// Columns: Service Name / Host / Uptime / Memory / CPU / Threads / Handles / Status
// + checkbox column + Start / Stop / Restart action buttons + total memory footer.

import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Play, Square, RotateCw, RefreshCw, Wrench } from 'lucide-react';
import { getToken } from '../lib/api';

const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });
const fJson = async (url: string, init: RequestInit = {}) => {
  const r = await fetch(url, { ...init, headers: { ...auth().headers, ...(init.headers || {}) } });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
};

export default function Services() {
  const qc = useQueryClient();
  const [picked, setPicked] = useState<Set<string>>(new Set());

  const { data, isFetching, refetch, dataUpdatedAt } = useQuery<any>({
    queryKey: ['services-detail'],
    queryFn: () => fJson('/api/troubleshoot/services-detail'),
    refetchInterval: 5000,
  });

  // Tick once a second so per-row uptime extrapolates live between polls.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const liveUptime = (s: number | null | undefined) =>
    typeof s === 'number' && dataUpdatedAt
      ? s + Math.floor((Date.now() - dataUpdatedAt) / 1000)
      : s ?? null;

  // Per-unit start / stop / restart
  const action = useMutation({
    mutationFn: ({ verb, unit }: { verb: 'start' | 'stop' | 'restart'; unit: string }) =>
      fJson(`/api/troubleshoot/services/${verb}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ unit }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['services-detail'] }),
  });

  function bulk(verb: 'start' | 'stop' | 'restart') {
    const units = Array.from(picked).filter(u => u !== 'pm2-internal' && !u.includes('worker') && !['cl_calls_ingestor','wakeup_calls','push_notifier','voicemail_ai_worker','cert_expiry_heartbeat','call_quality_alerts','live_call_sentiment','stir_inbound_hook','options_ping','phone_reboot','firmware_advisor','auto_queue_logout'].includes(u));
    if (units.length === 0) {
      alert('Pick at least one top-level service (workers run inside stratus-api).');
      return;
    }
    if (!confirm(`${verb} ${units.length} service${units.length === 1 ? '' : 's'}?`)) return;
    units.forEach(u => action.mutate({ verb, unit: u }));
  }

  function toggle(unit: string) {
    const next = new Set(picked);
    if (next.has(unit)) next.delete(unit); else next.add(unit);
    setPicked(next);
  }
  function toggleAll() {
    if (!data?.services) return;
    if (picked.size > 0) setPicked(new Set());
    else setPicked(new Set(data.services.map((s: any) => s.unit)));
  }

  const services: any[] = data?.services || [];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold flex items-center gap-2"><Wrench /> Services</h1>
        <button className="btn-ghost text-xs" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw size={14} className={`inline mr-1 ${isFetching ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      <div className="flex gap-2">
        <button className="btn-primary text-sm inline-flex items-center gap-1" onClick={() => bulk('start')}>
          <Play size={14} /> Start
        </button>
        <button className="btn text-sm inline-flex items-center gap-1" onClick={() => bulk('stop')}>
          <Square size={14} /> Stop
        </button>
        <button className="btn text-sm inline-flex items-center gap-1" onClick={() => bulk('restart')}>
          <RotateCw size={14} /> Restart
        </button>
      </div>

      <div className="card p-0 overflow-hidden">
        <table className="table">
          <thead>
            <tr>
              <th><input type="checkbox" checked={picked.size > 0 && picked.size === services.length} onChange={toggleAll} /></th>
              <th>Service Name</th>
              <th>Host</th>
              <th className="text-right">Uptime</th>
              <th className="text-right">Memory Usage</th>
              <th className="text-right">CPU Usage</th>
              <th className="text-right">Threads</th>
              <th className="text-right">Handles</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {services.map((s: any) => {
              const down = s.status !== 'Running' && s.status !== 'Unknown' && s.kind !== 'worker';
              const rowCls = down
                ? 'bg-danger/10 hover:bg-danger/15 border-l-2 border-l-danger'
                : `hover:bg-elevated ${s.kind === 'worker' ? 'text-textSecondary text-sm' : ''}`;
              return (
              <tr key={s.unit} className={rowCls} title={s.error || s.note || ''}>
                <td><input type="checkbox" checked={picked.has(s.unit)} onChange={() => toggle(s.unit)} disabled={s.kind === 'worker'} /></td>
                <td>
                  {s.kind === 'worker' && <span className="ml-4 text-xs">↳ </span>}
                  {s.name}
                  {s.pid && <span className="text-xs text-textSecondary ml-2">pid {s.pid}</span>}
                  {s.restarts != null && s.restarts > 0 && <span className="text-xs text-warning ml-2">{s.restarts} restarts</span>}
                  {s.version && <span className="text-xs text-textSecondary ml-2 font-mono">{shortVersion(s.version)}</span>}
                </td>
                <td className="text-xs font-mono text-textSecondary">{s.host || 'local'}</td>
                <td className="text-right font-mono text-sm text-textSecondary">{formatUptime(liveUptime(s.uptime_seconds))}</td>
                <td className="text-right font-mono text-sm">{formatBytes(s.memory_bytes)}</td>
                <td className="text-right font-mono text-sm">{s.cpu_pct ? `${s.cpu_pct.toFixed(1)} %` : '—'}</td>
                <td className="text-right font-mono text-sm">{s.threads || '—'}</td>
                <td className="text-right font-mono text-sm">{s.handles || '—'}</td>
                <td>
                  <span className={`badge ${statusClass(s.status)}`}>
                    {s.status}
                  </span>
                </td>
                <td className="space-x-1 whitespace-nowrap">
                  {s.kind !== 'worker' && (
                    <>
                      {s.status !== 'Running' && (
                        <button className="btn-ghost p-1" title="Start" onClick={() => action.mutate({ verb: 'start', unit: s.unit })}>
                          <Play size={12} className="text-success" />
                        </button>
                      )}
                      {s.status === 'Running' && (
                        <button className="btn-ghost p-1" title="Stop" onClick={() => confirm(`Stop ${s.name}?`) && action.mutate({ verb: 'stop', unit: s.unit })}>
                          <Square size={12} className="text-danger" />
                        </button>
                      )}
                      <button className="btn-ghost p-1" title="Restart" onClick={() => confirm(`Restart ${s.name}?`) && action.mutate({ verb: 'restart', unit: s.unit })}>
                        <RotateCw size={12} />
                      </button>
                    </>
                  )}
                </td>
              </tr>
              );
            })}
            {services.length === 0 && <tr><td colSpan={10} className="text-center py-6 text-textSecondary">Loading services…</td></tr>}
          </tbody>
          {data?.total_memory_bytes > 0 && (
            <tfoot>
              <tr className="font-semibold">
                <td colSpan={4} className="text-right">Total</td>
                <td className="text-right font-mono">{formatBytes(data.total_memory_bytes)}</td>
                <td colSpan={5}></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <p className="text-xs text-textSecondary">
        Updates every 5s. The <b>Host</b> column shows where each service runs —
        services tagged <code className="bg-elevated px-1 mx-0.5 rounded">local</code>
        live on this LXC, others run on the named host (typically a separate
        media/SIP server). Workers (rows starting with ↳) run inside the
        <code className="bg-elevated px-1 mx-1 rounded">stratus-api</code> Node process and share its memory and uptime;
        restart that single service to bounce them all together.
      </p>
    </div>
  );
}

function formatBytes(b: number | undefined): string {
  if (!b) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function formatUptime(s: number | null | undefined): string {
  if (s == null || s < 0) return '—';
  const sec = s % 60;
  if (s < 60) return `${sec}s`;
  const min = Math.floor(s / 60) % 60;
  if (s < 3600) return `${min}m ${sec}s`;
  const hour = Math.floor(s / 3600) % 24;
  if (s < 86400) return `${hour}h ${min}m ${sec}s`;
  const days = Math.floor(s / 86400);
  return `${days}d ${hour}h ${min}m ${sec}s`;
}

function statusClass(status: string): string {
  switch (status) {
    case 'Running':     return 'bg-success/20 text-success';
    case 'Stopped':     return 'bg-danger/20 text-danger';
    case 'Unreachable': return 'bg-danger/20 text-danger';
    case 'Error':       return 'bg-danger/20 text-danger';
    case 'Unknown':     return 'bg-warning/20 text-warning';
    default:            return 'bg-warning/20 text-warning';
  }
}

// Shrink "FreeSWITCH Version 1.10.12-release+git~20260109T171519Z~ef885e7984~64bit"
// to "1.10.12" for inline display (full string available in fs_version on dashboard).
function shortVersion(v: string): string {
  const m = v.match(/(\d+\.\d+(?:\.\d+)?)/);
  return m ? `v${m[1]}` : v.slice(0, 24);
}
